import { Injectable, inject } from '@angular/core';
import { filter, take } from 'rxjs/operators';
import { PlaybackService } from './playback.service';
import { APP_VERSION } from '../../version';
import { environment } from '../../../environments/environment';

// Detects that this tab is running a stale bundle (long-lived tab, or cached
// chunks) by comparing the compiled-in APP_VERSION against the backend's view
// of the current version, and reloads the page to pick up the new code.
// A reload is deferred while music is playing — it would kill the web player —
// and happens at the next check once playback is idle.
@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  private playbackService = inject(PlaybackService);
  private started = false;
  private reloadPending = false;
  private readonly CHECK_INTERVAL_MS = 5 * 60 * 1000;

  public start(): void {
    if (this.started || typeof window === 'undefined') return;
    this.started = true;

    void this.check('startup');
    setInterval(() => void this.check('interval'), this.CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        void this.check('tab-visible');
      }
    });
  }

  private async check(trigger: string): Promise<void> {
    if (this.reloadPending) {
      this.maybeReload(trigger);
      return;
    }
    try {
      const response = await fetch(`${environment.apiUrl}/version`, {
        credentials: 'include',
        cache: 'no-store'
      });
      if (!response.ok) return;
      const { version } = await response.json();
      if (!version || version === 'unknown' || version === APP_VERSION) return;
      console.log(`[version] running ${APP_VERSION} but backend has ${version} — reload needed (${trigger})`);
      this.reloadPending = true;
      // Reload the moment playback goes idle rather than waiting for the next check.
      this.playbackService.isPlayingCombined$.pipe(
        filter(isPlaying => !isPlaying),
        take(1)
      ).subscribe(() => this.maybeReload('playback-idle'));
      this.maybeReload(trigger);
    } catch {
      // Network errors are irrelevant here — next check will retry.
    }
  }

  private maybeReload(trigger: string): void {
    const state = this.playbackService.getCombinedPlaybackState();
    if (state?.isPlaying) {
      console.log(`[version] reload deferred — music is playing (${trigger})`);
      return;
    }
    console.log(`[version] reloading to pick up new bundle (${trigger})`);
    window.location.reload();
  }
}
